import {
  registerWeatherEffects,
  deregisterWeatherEffects,
} from './repositories/weather.effects';
import {
  fetchWeatherAlertsSettings,
  saveWeatherAlertsSettings,
} from './repositories/weather.actions';
import {
  weatherAlertsInitialized$,
  weatherAlertsSaved$,
  weatherAlertsSnapshot,
} from './repositories/weather.repository';
import { getRoadRiskOptions } from './utils/get-road-risk-options';
import { getTippingIndexOptions } from './utils/get-tipping-index-options';
import { updateToast } from '../../repositories/toasts.repository';
import { trackFormChanges } from '../../utils/track-form-changes';
import { amsFeaturesStoreSnapshot } from '../../repositories/ams-features/ams-features.repository';
import { DecimalInputDirective } from '../../directives/decimal-input.directive';
import { BrazeEventsService } from '../../services/braze-events.service';
import { FormRawValue } from '../../models/form-utility-types';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  InitializationState,
  subscribeOnce,
  SubscriptionManager,
} from '@tmx/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { tap, pairwise, combineLatest, map } from 'rxjs';
import { dispatch } from '@ngneat/effects';
import {
  InlineHelpComponent,
  MeterComponent,
  Range,
  RangeSliderComponent,
  SliderComponent,
} from '@tmx/operandi';
import { WeatherAlerts } from '@tmx/data-access';
import { deepEqual } from 'fast-equals';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { translate, TranslocoModule } from '@jsverse/transloco';
import { WithValidationsDirective } from '@shared/core-utils';
import { MessageComponent } from '@tmx/modus-operandi';

const TRIGGER_DELAY = 250;
const DEFAULT_ROAD_RISK_THRESHOLD = 4;

@Component({
  selector: 'weather',
  templateUrl: './weather.component.html',
  styleUrls: ['./weather.component.scss'],
  imports: [
    TranslocoModule,
    ReactiveFormsModule,
    SliderComponent,
    MeterComponent,
    RangeSliderComponent,
    WithValidationsDirective,
    InlineHelpComponent,
    DecimalInputDirective,
    TooltipModule,
    MessageComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherComponent implements OnInit, OnDestroy {
  private readonly brazeEventsService = inject(BrazeEventsService);
  private readonly sm = new SubscriptionManager();
  private pulseSummaryTimeoutId = 0;

  readonly canEditWeather = amsFeaturesStoreSnapshot().canEditWeather;
  initializationState = signal<InitializationState>('loading');

  readonly roadRiskOptions = signal(getRoadRiskOptions());
  roadRiskThresholdControl = new FormControl(0, {
    nonNullable: true,
  });

  readonly tippingIndexOptions = signal(getTippingIndexOptions());
  tippingIndexThresholdControl = new FormControl(0, {
    nonNullable: true,
  });
  enableTippingIndexControl = new FormControl(false, {
    nonNullable: true,
  });

  readonly MIN_WIND_SPEED = 0;
  readonly MAX_WIND_SPEED = 200;
  minWindSpeedControl = new FormControl(0, {
    nonNullable: true,
  });
  enableMinWindSpeed = new FormControl(false, {
    nonNullable: true,
  });

  readonly MIN_WIND_GUST = 0;
  readonly MAX_WIND_GUST = 200;
  minWindGustControl = new FormControl(0, {
    nonNullable: true,
  });
  enableMinWindGust = new FormControl(false, {
    nonNullable: true,
  });

  readonly MIN_TEMP_THRESHOLD = -50;
  readonly MAX_TEMP_THRESHOLD = 150;
  temperatureThresholdControl = new FormControl(
    new Range(this.MIN_TEMP_THRESHOLD, this.MAX_TEMP_THRESHOLD),
    {
      nonNullable: true,
    }
  );
  enableTempThresholdControl = new FormControl(false, {
    nonNullable: true,
  });

  readonly MIN_VISIBILITY = 0;
  readonly MAX_VISIBILITY = 50;
  minVisibilityControl = new FormControl(0, {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.min(this.MIN_VISIBILITY),
      Validators.max(this.MAX_VISIBILITY),
    ],
  });
  enableMinVisibility = new FormControl(false, {
    nonNullable: true,
  });

  weatherAlertsForm = new FormGroup({
    roadRiskThreshold: this.roadRiskThresholdControl,

    tippingIndexThreshold: this.tippingIndexThresholdControl,
    enableTippingIndex: this.enableTippingIndexControl,

    minWindSpeed: this.minWindSpeedControl,
    enableMinWindSpeed: this.enableMinWindSpeed,

    minWindGust: this.minWindGustControl,
    enableMinWindGust: this.enableMinWindGust,

    temperatureThreshold: this.temperatureThresholdControl,
    enableTempThreshold: this.enableTempThresholdControl,

    minVisibility: this.minVisibilityControl,
    enableMinVisibility: this.enableMinVisibility,
  });

  initialFormValue = signal(this.weatherAlertsForm.getRawValue());

  saving = signal(false);
  formUnchanged = trackFormChanges(
    this.weatherAlertsForm,
    this.initialFormValue,
    {
      resetFormOnUnchangedValue: false,
    }
  );

  pulseSummary = signal(false);

  disableClearOverrides = toSignal(
    combineLatest([
      this.enableMinWindSpeed.valueChanges,
      this.enableMinWindGust.valueChanges,
      this.enableTempThresholdControl.valueChanges,
      this.enableTippingIndexControl.valueChanges,
      this.enableMinVisibility.valueChanges,
    ]).pipe(map((overrides) => overrides.every((override) => !override)))
  );

  ngOnInit() {
    registerWeatherEffects();
    this.brazeEventsService.logBrazeCustomEvents(
      this.brazeEventsService.LOADED_WEATHER_CONFIGURATION
    );

    this.loadSettings();

    this.sm.manage = this.weatherAlertsForm.valueChanges
      .pipe(
        pairwise(),
        tap(([val1, val2]) => {
          if (!deepEqual(val1, val2)) {
            this.handleFormChanges(this.weatherAlertsForm.getRawValue());
          }
        })
      )
      .subscribe();

    this.sm.manage = this.enableMinVisibility.valueChanges
      .pipe(
        tap((enabled) => {
          if (enabled) {
            return;
          }

          const { minVisibility } = this.initialFormValue();
          this.minVisibilityControl.setValue(minVisibility);
        })
      )
      .subscribe();
  }

  loadSettings() {
    this.initializationState.set('loading');

    dispatch(fetchWeatherAlertsSettings());

    subscribeOnce(weatherAlertsInitialized$, {
      next: (initialized) => {
        if (initialized) {
          this.populateForm(weatherAlertsSnapshot());
        }
        this.initializationState.set(initialized ? 'initialized' : 'failed');
      },
    });
  }

  save() {
    this.saving.set(true);

    const weatherAlerts = this.mapFormToWeatherAlerts(
      this.weatherAlertsForm.getRawValue()
    );

    dispatch(
      saveWeatherAlertsSettings({
        weatherAlerts,
      })
    );

    subscribeOnce(weatherAlertsSaved$, {
      next: (saved) => {
        this.saving.set(false);

        if (saved) {
          updateToast({
            type: 'success',
            content: translate('weather.weather.settingsSaved'),
          });
          this.populateForm(weatherAlerts);
        } else {
          updateToast({
            type: 'danger',
            content: translate('weather.weather.settingsFailedToSave'),
          });
        }
      },
    });
  }

  openSupportArticle() {
    window.open(
      'https://support.copilotpro.com/en/support/solutions/articles/19000160315-account-manager-weather'
    );
  }

  clearOverrides() {
    this.weatherAlertsForm.patchValue({
      enableMinWindSpeed: false,
      enableMinWindGust: false,
      enableTempThreshold: false,
      enableTippingIndex: false,
      enableMinVisibility: false,
    });
    this.weatherAlertsForm.markAsDirty();
    this.handleFormChanges(this.weatherAlertsForm.getRawValue());
  }

  private populateForm(weatherAlerts: WeatherAlerts) {
    if (typeof weatherAlerts.minRoadIndex === 'number') {
      this.roadRiskThresholdControl.setValue(weatherAlerts.minRoadIndex);
    } else {
      this.roadRiskThresholdControl.setValue(DEFAULT_ROAD_RISK_THRESHOLD);
    }

    if (typeof weatherAlerts.minTippingIndex === 'number') {
      this.tippingIndexThresholdControl.setValue(weatherAlerts.minTippingIndex);
      this.enableTippingIndexControl.setValue(true);
    } else {
      this.enableTippingIndexControl.setValue(false);
    }

    if (typeof weatherAlerts.minWindSpeed === 'number') {
      this.minWindSpeedControl.setValue(weatherAlerts.minWindSpeed);
      this.enableMinWindSpeed.setValue(true);
    }

    if (typeof weatherAlerts.minWindGust === 'number') {
      this.minWindGustControl.setValue(weatherAlerts.minWindGust);
      this.enableMinWindGust.setValue(true);
    }

    if (
      typeof weatherAlerts.minTemperature === 'number' ||
      typeof weatherAlerts.maxTemperature === 'number'
    ) {
      const range = new Range(
        weatherAlerts.minTemperature ?? this.MIN_TEMP_THRESHOLD,
        weatherAlerts.maxTemperature ?? this.MAX_TEMP_THRESHOLD
      );

      this.temperatureThresholdControl.setValue(range);
      this.enableTempThresholdControl.setValue(true);
    }

    if (typeof weatherAlerts.minVisibility === 'number') {
      this.minVisibilityControl.setValue(weatherAlerts.minVisibility);
      this.enableMinVisibility.setValue(true);
    }

    this.handleFormChanges(this.weatherAlertsForm.getRawValue());
    this.initialFormValue.set(this.weatherAlertsForm.getRawValue());
    this.weatherAlertsForm.reset(this.initialFormValue());

    if (!this.canEditWeather) {
      this.weatherAlertsForm.disable({ emitEvent: true });
    }
  }

  private handleFormChanges(
    formChanges: FormRawValue<typeof this.weatherAlertsForm>
  ) {
    this.triggerSummaryPulse();

    if (formChanges.enableTippingIndex) {
      this.tippingIndexThresholdControl.enable();
    } else {
      this.tippingIndexThresholdControl.disable();
    }

    if (formChanges.enableMinWindSpeed) {
      this.minWindSpeedControl.enable();
    } else {
      this.minWindSpeedControl.disable();
    }

    if (formChanges.enableMinWindGust) {
      this.minWindGustControl.enable();
    } else {
      this.minWindGustControl.disable();
    }

    if (formChanges.enableTempThreshold) {
      this.temperatureThresholdControl.enable();
    } else {
      this.temperatureThresholdControl.disable();
    }

    if (formChanges.enableMinVisibility) {
      this.minVisibilityControl.enable();
    } else {
      this.minVisibilityControl.disable();
    }
  }

  private mapFormToWeatherAlerts(
    form: FormRawValue<typeof this.weatherAlertsForm>
  ) {
    const weatherAlerts: WeatherAlerts = {
      minRoadIndex: form.roadRiskThreshold,
      minTippingIndex: form.enableTippingIndex
        ? form.tippingIndexThreshold
        : null,
      minWindSpeed: form.enableMinWindSpeed ? form.minWindSpeed : null,
      minWindGust: form.enableMinWindGust ? form.minWindGust : null,
      minTemperature: form.enableTempThreshold
        ? form.temperatureThreshold.min
        : null,
      maxTemperature: form.enableTempThreshold
        ? form.temperatureThreshold.max
        : null,
      minVisibility: form.enableMinVisibility ? form.minVisibility : null,
    };

    return weatherAlerts;
  }

  private triggerSummaryPulse() {
    if (this.weatherAlertsForm.pristine) {
      return;
    }

    if (this.pulseSummary()) {
      this.pulseSummary.set(false);
    }

    if (this.pulseSummaryTimeoutId > 0) {
      window.clearTimeout(this.pulseSummaryTimeoutId);
    }

    this.pulseSummaryTimeoutId = window.setTimeout(() => {
      this.pulseSummaryTimeoutId = 0;

      this.pulseSummary.set(true);
    }, TRIGGER_DELAY);
  }

  ngOnDestroy() {
    this.sm.unsubscribe();
    deregisterWeatherEffects();
  }
}
